import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/student/PageHeader";
import { Colors, useLayoutCommon } from "@/contexts/LayoutContext";
import { Button } from "@/components/ui";
import { Spinner } from "@/components/ui/spinner";
import { Award, TrendingUp, Home, RotateCcw, Package } from "lucide-react";
import { usePrivate } from "@/contexts/PrivateContext";
import { LevelTestStates, LevelTestResult } from "@/services/student/level-test.interfaces";
import { useLevelTest, useLevelTestResult } from "@/hooks/student/useLevelTest";
import { useLevelTestContext } from "@/contexts/LevelTestContext";
import { getLevelTestResult, getLevelTestStatus } from "@/services/student/level-test.service";
import { getAuthToken } from "@/lib/authUtils";
import { hasPurchasedPackages } from "@/services/student/packages.service";

// Helper para extrair userId do JWT token
const getUserIdFromToken = (): string | null => {
  try {
    const token = getAuthToken();
    if (!token) return null;
    
    const tokenParts = token.split('.');
    if (tokenParts.length < 2) return null;
    
    const base64Url = tokenParts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = atob(base64);
    const payload = JSON.parse(jsonPayload);
    
    // O userId está no claim customizado
    return payload['http://schemas.ofcourse.org/ws/2025/01/identity/claims/uid'] || null;
  } catch {
    return null;
  }
};

interface TestResultProps {
  level?: string;
  score?: number;
  correctAnswers?: number;
}

export const TestResult: React.FC<TestResultProps> = ({
  level: propLevel,
  score: propScore,
  correctAnswers: propCorrectAnswers
}) => {
  const { setBackgroundColor } = useLayoutCommon();
  const navigate = useNavigate();
  const { updateUser, state: userState } = usePrivate();
  const { resetTest, sessionId } = useLevelTest();
  const { result: contextResult, hasResult } = useLevelTestResult();
  const { actions: contextActions } = useLevelTestContext();
  
  // Estado local para o resultado buscado da API
  const [fetchedResult, setFetchedResult] = useState<LevelTestResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  
  // Estado para verificação de pacotes
  const [hasPackages, setHasPackages] = useState<boolean | null>(null);
  const [isCheckingPackages, setIsCheckingPackages] = useState(false);
  const hasCheckedPackagesRef = useRef(false);

  // Usar userId do state (preferencial) ou fallback do token
  const userId = userState?.id || getUserIdFromToken();
  const isUserLoaded = userState?.isLoaded ?? false;

  // Buscar resultado da API se não estiver no contexto (apenas uma vez)
  useEffect(() => {
    console.log('[TestResult] useEffect triggered:', {
      hasFetchedRef: hasFetchedRef.current,
      hasResult,
      sessionId,
      userId,
      isUserLoaded,
      userStateId: userState?.id
    });
    
    // Evitar múltiplas buscas
    if (hasFetchedRef.current || hasResult) {
      console.log('[TestResult] Skipping fetch - already fetched or has result');
      return;
    }

    // Esperar o PrivateContext carregar OU ter um userId disponível (do token)
    if (!isUserLoaded && !userId) {
      console.log('[TestResult] Waiting for user to load...');
      return;
    }

    const fetchResult = async () => {
      hasFetchedRef.current = true;
      setIsLoading(true);
      console.log('[TestResult] Starting fetch with userId:', userId, '(from state:', userState?.id, ')');
      
      try {
        let targetSessionId = sessionId;
        
        // Se não há sessionId, buscar o status para obter lastCompletedSessionId
        if (!targetSessionId && userId) {
          console.log('[TestResult] Fetching status for userId:', userId);
          const status = await getLevelTestStatus({ userId });
          console.log('[TestResult] Status received:', status);
          targetSessionId = status?.lastCompletedSessionId || null;
        }
        
        if (targetSessionId) {
          console.log('[TestResult] Fetching result for sessionId:', targetSessionId);
          const result = await getLevelTestResult({ sessionId: targetSessionId });
          console.log('[TestResult] Result received:', result);
          setFetchedResult(result);
          contextActions.completeTest(result);
        } else {
          console.log('[TestResult] No targetSessionId available');
        }
      } catch (error) {
        console.error('[TestResult] Erro ao buscar resultado:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Executar fetch se temos sessionId ou userId
    if (sessionId || userId) {
      fetchResult();
    } else {
      console.log('[TestResult] No sessionId or userId available after load');
    }
  }, [sessionId, userId, isUserLoaded, hasResult, contextActions, userState?.id]);

  // Usar resultado do contexto ou do fetch local
  const result = contextResult || fetchedResult;
  
  // Determinar se está carregando: isLoading OU (não tem resultado E ainda não tentou buscar)
  const isDataLoading = isLoading || (!result && !hasFetchedRef.current && (sessionId || userId));

  // Usar dados do resultado se disponível, senão usar props ou defaults
  const level = useMemo(() => {
    if (result?.finalLevelName) return result.finalLevelName;
    if (result?.finalLevel) {
      // Fallback: converter número para nome se necessário
      const levelMap: Record<string, string> = {
        "1": "Beginner",
        "2": "Elementary",
        "3": "Intermediate",
        "4": "Upper Intermediate",
        "5": "Advanced"
      };
      return levelMap[result.finalLevel.toString()] || result.finalLevel.toString();
    }
    return propLevel || "Intermediate";
  }, [result, propLevel]);

  const score = useMemo(() => {
    if (result?.scorePercentage !== undefined) return result.scorePercentage;
    return propScore ?? 75;
  }, [result, propScore]);

  const correctAnswers = useMemo(() => {
    if (result?.correctAnswers !== undefined && result.correctAnswers !== null) {
      return result.correctAnswers;
    }
    return propCorrectAnswers ?? 8;
  }, [result, propCorrectAnswers]);

  // Definir cor de fundo apenas no mount
  useEffect(() => {
    setBackgroundColor(Colors.WHITE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verificar se estudante tem pacotes após resultados serem carregados
  useEffect(() => {
    // Só verificar se já temos resultado e ainda não verificamos
    if (!result || hasCheckedPackagesRef.current || isCheckingPackages) {
      return;
    }

    const checkPackages = async () => {
      hasCheckedPackagesRef.current = true;
      setIsCheckingPackages(true);
      
      try {
        const hasPkgs = await hasPurchasedPackages();
        setHasPackages(hasPkgs);
        
        // Se não tem pacotes, redirecionar após 3 segundos
        if (!hasPkgs) {
          setTimeout(() => {
            updateUser({ levelTestState: LevelTestStates.Completed });
            resetTest();
            navigate('/student/packages');
          }, 3000);
        }
      } catch (error) {
        console.error('[TestResult] Error checking packages:', error);
        // Em caso de erro, assumir que tem pacotes para manter comportamento padrão
        setHasPackages(true);
      } finally {
        setIsCheckingPackages(false);
      }
    };

    checkPackages();
  }, [result, navigate, updateUser, resetTest, isCheckingPackages]);

  const handleGoToDashboard = () => {
    // Garantir que o guard não bloqueie a navegação
    updateUser({ levelTestState: LevelTestStates.Completed });
    // Limpar estado local do teste para evitar efeitos colaterais
    resetTest();
    navigate('/student/dashboard');
  };

  const handleViewPackages = () => {
    // Garantir que o guard não bloqueie a navegação
    updateUser({ levelTestState: LevelTestStates.Completed });
    // Limpar estado local do teste para evitar efeitos colaterais
    resetTest();
    navigate('/student/packages');
  };

  const handleRetakeTest = () => {
    navigate('/student/level-test/welcome');
  };

  const getLevelDescription = (level: string) => {
    const descriptions = {
      'Beginner': 'Você está no nível inicial. Recomendamos começar com módulos básicos de gramática e vocabulário.',
      'Elementary': 'Você tem conhecimentos básicos. Foque em consolidar estruturas fundamentais.',
      'Intermediate': 'Você tem um bom domínio intermediário. Trabalhe para avançar para o próximo nível.',
      'Upper Intermediate': 'Você está quase no nível avançado. Refine sua fluência e precisão.',
      'Advanced': 'Parabéns! Você tem um excelente domínio da língua. Continue praticando para manter o nível.',
      'Proficient': 'Você é proficiente em inglês. Foque em aspectos específicos que deseja melhorar.'
    };
    return descriptions[level as keyof typeof descriptions] || 'Continue praticando para melhorar seu nível!';
  };

  const getLevelColor = (level: string) => {
    const colors = {
      'Beginner': 'text-green-600 bg-green-50 border-green-200',
      'Elementary': 'text-blue-600 bg-blue-50 border-blue-200',
      'Intermediate': 'text-purple-600 bg-purple-50 border-purple-200',
      'Upper Intermediate': 'text-orange-600 bg-orange-50 border-orange-200',
      'Advanced': 'text-red-600 bg-red-50 border-red-200',
      'Proficient': 'text-indigo-600 bg-indigo-50 border-indigo-200'
    };
    return colors[level as keyof typeof colors] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  // Mostrar spinner enquanto carrega
  if (isDataLoading) {
    return (
      <div className="max-w-[1800px] w-full">
        <PageHeader
          showAvatar={true}
          showActions={true}
          title="Resultado do Teste"
          progressInfo={{
            label: "Carregando",
            value: "..."
          }}
        />
        <div className="w-full bg-gray-50 rounded-xl p-6 mt-6">
          <div className="w-[1227px] mx-auto">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col items-center justify-center py-20">
                <Spinner size="lg" className="text-blue-600 mb-4" />
                <p className="text-gray-600 text-lg">Carregando resultados do teste...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1800px] w-full">
      <PageHeader
        showAvatar={true}
        showActions={true}
        title="Resultado do Teste"
        progressInfo={{
          label: "Pontuação",
          value: `${score}%`
        }}
      />

      <div className="w-full bg-gray-50 rounded-xl p-6 mt-6">
        <div className="w-[1227px] mx-auto">
          <div className="max-w-4xl mx-auto">
            {/* Animação de celebração */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-4 animate-bounce">
                <Award className="w-10 h-10 text-yellow-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Parabéns! Teste Concluído
              </h2>
              <p className="text-lg text-gray-600">
                Aqui estão os resultados do seu teste de nivelamento
              </p>
            </div>

            {/* Card principal com resultado */}
            <div className={`rounded-lg border-2 p-8 mb-8 text-center ${getLevelColor(level)}`}>
              <div className="mb-6">
                <div className="text-6xl font-bold mb-2">{level}</div>
                <div className="text-xl font-semibold">Seu Nível de Inglês</div>
              </div>

              <div className="flex justify-center items-center gap-8 mb-6">
                <div>
                  <div className="text-3xl font-bold">{score}%</div>
                  <div className="text-sm opacity-80">Pontuação Total</div>
                </div>
                <div className="w-px h-12 bg-current opacity-30"></div>
                <div>
                  <div className="text-3xl font-bold">{correctAnswers}</div>
                  <div className="text-sm opacity-80">Questões Corretas</div>
                </div>
              </div>

              <p className="text-base leading-relaxed max-w-2xl mx-auto">
                {getLevelDescription(level)}
              </p>
            </div>

            {/* Estatísticas detalhadas */}
            {result?.skillBreakdown?.skills && Object.keys(result.skillBreakdown.skills).length > 0 && (
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Desempenho por Área
                  </h3>
                </div>
                <div className="space-y-3">
                  {Object.entries(result.skillBreakdown.skills).map(([skillName, skillPercentage]) => (
                    <div key={skillName} className="flex justify-between items-center">
                      <span className="text-gray-600 capitalize">{skillName}</span>
                      <span className="font-medium">{Math.round(skillPercentage)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Botões de ação */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {hasPackages === false ? (
                // Estudante não tem pacotes - mostrar botão para ver pacotes
                <>
                  <Button
                    onClick={handleViewPackages}
                    className="px-8 py-3 text-lg font-semibold bg-blue-600 hover:bg-blue-700"
                  >
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Ver Pacotes Disponíveis
                    </div>
                  </Button>
                  <p className="text-center text-sm text-gray-600 mt-2">
                    Você será redirecionado automaticamente em alguns segundos...
                  </p>
                </>
              ) : (
                // Estudante tem pacotes ou ainda está verificando - mostrar botão para dashboard
                <Button
                  onClick={handleGoToDashboard}
                  className="px-8 py-3 text-lg font-semibold bg-blue-600 hover:bg-blue-700"
                  disabled={isCheckingPackages}
                >
                  <div className="flex items-center gap-2">
                    {isCheckingPackages ? (
                      <>
                        <Spinner size="sm" />
                        Verificando...
                      </>
                    ) : (
                      <>
                        <Home className="w-5 h-5" />
                        Ir para Dashboard
                      </>
                    )}
                  </div>
                </Button>
              )}

              {/* <Button
                onClick={handleRetakeTest}
                variant="outline"
                className="px-8 py-3 text-lg font-semibold border-gray-300 hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <RotateCcw className="w-5 h-5" />
                  Refazer Teste
                </div>
              </Button> */}
            </div>

            {/* Nota final */}
            <div className="text-center mt-8 text-gray-500">
              <p className="text-sm">
                Seus resultados foram salvos e podem ser consultados no seu perfil a qualquer momento.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
